import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useFlowSelection } from "@flows/state/FlowSelectionProvider";
import { useNodeSelection } from "@/context/node-selection";
import { useRunControl } from "@/context/run-control";
import { findStoryById } from "@features/devtools/registry";

export type Crumb = { key: string; label: string; href?: string };

const titleize = (s: string) =>
  s
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\s+(\d+)/g, " $1");

export function useBreadcrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const { selectedFlowId, selectedFlow } = useFlowSelection();
  const { selectedNode } = useNodeSelection();
  const { runId, status: runStatus } = useRunControl();

  return useMemo(() => {
    const items: Crumb[] = [];
    const segs = pathname.split("/").filter(Boolean);

    // Root brand always links to flows (since "/" redirects)
    items.push({ key: "root", label: "Bunker", href: "/flows" });

    if (segs[0] === "flows") {
      items.push({ key: "flows", label: "Flows", href: "/flows" });

      // /flows/tests
      if (segs[1] === "tests") {
        items.push({ key: "flows-tests", label: "Test Suite" });
        return items;
      }

      // /flows/:flowId  (percent-encoded; may contain slashes)
      if (segs[1]) {
        const encodedId = segs[1];
        const flowIdFromUrl = decodeURIComponent(encodedId);
        const flow = selectedFlowId ? selectedFlow : undefined;

        const catSegments = flow?.category
          ? flow.category.split("/").filter(Boolean)
          : flowIdFromUrl.split("/").slice(0, -1);

        catSegments.forEach((seg, i) => {
          items.push({ key: `flow-cat-${i}`, label: titleize(seg) });
        });

        const flowLeaf =
          flow?.title ??
          titleize(flowIdFromUrl.split("/").slice(-1)[0] || "Flow");

        items.push({ key: "flow-leaf", label: flowLeaf });

        // Optional "Node: <label>" when a node is selected in canvas
        if (selectedNode) {
          const nodeLabel = (selectedNode as any)?.data?.label ?? selectedNode.id;
          items.push({ key: "node", label: `Node: ${nodeLabel}` });
        }

        // Optional "Run <id>" when a run is active
        if (runId) {
          const short = String(runId).slice(0, 6);
          const runText = runStatus ? `Run ${short} (${runStatus})` : `Run ${short}`;
          items.push({ key: "run", label: runText });
        }

        return items;
      }

      // /flows
      return items;
    }

    if (segs[0] === "dev") {
      items.push({ key: "dev", label: "DevTools", href: "/dev" });

      // /dev/api
      if (segs[1] === "api") {
        items.push({ key: "dev-api", label: "API Tester" });
        return items;
      }

      // /dev/:storyId
      if (segs[1]) {
        const storyId = decodeURIComponent(segs[1]);
        const story = findStoryById(storyId);

        if (story?.group) {
          story.group.split("/").forEach((g, i) => {
            items.push({ key: `dev-group-${i}`, label: g });
          });
        }

        items.push({
          key: "dev-story",
          label: story?.title ?? storyId.split("/").slice(-1)[0],
        });
        return items;
      }

      return items;
    }

    // Fallback (unknown top-level): show just brand
    return items;
  }, [pathname, selectedFlowId, selectedFlow, selectedNode, runId, runStatus]);
}

